/**
 * schema.org JSON-LD builders for the public pages (Phase 5d SEO). Pure functions: they
 * return plain objects (or null when there is nothing true to say); <SEO jsonLd={...}>
 * serialises them into one <script type="application/ld+json"> in the head.
 *
 * Real data only: every value comes from the page's own data (API tiers, the business
 * record) or from facts already published on the site (name, logo, the Footer's social
 * links). No ratings, counts or availability claims are invented, and Google ratings are
 * never re-published as aggregateRating (Google's review-snippet policy allows first-party
 * reviews only).
 *
 * FAQPage JSON-LD is not built here: FaqList renders it next to the questions
 * (components/site/FaqList.jsx buildFaqJsonLd), so the markup always matches the list.
 */
import { PAID_SLOT_TIERS, quarterlyCents } from './slotPricing';

export const SITE_URL = 'https://tappd.app';
export const SITE_NAME = 'Tappd';
export const SCHEMA_CONTEXT = 'https://schema.org';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
/** The brand logo in public/ (also the apple-touch-icon and the Header/Footer logo). */
export const LOGO_PATH = '/tappd-logo.png';
/** The social profiles the Footer links to (components/Footer.jsx); a test keeps them in sync. */
export const SOCIAL_PROFILES = ['https://www.instagram.com/tappd.app', 'https://facebook.com/tappdapp'];

const TIER_NAMES = { standard: 'Standard', premium: 'Premium', sponsor: 'Venue Sponsor' };

/**
 * One JSON-LD script body from an object or an array of objects. An array with more than
 * one node becomes a single `@graph` (the nodes' own @context is dropped in favour of the
 * graph's). "<" is escaped as < so no value can close the script tag or open an HTML
 * comment; JSON.parse restores it. Returns null when there is nothing to render.
 */
export function serializeJsonLd(data) {
  if (!data) return null;
  let value = data;
  if (Array.isArray(data)) {
    const nodes = data.filter((n) => n && typeof n === 'object');
    if (!nodes.length) return null;
    value = nodes.length === 1
      ? nodes[0]
      : {
        '@context': SCHEMA_CONTEXT,
        // eslint-disable-next-line no-unused-vars
        '@graph': nodes.map(({ '@context': ctx, ...rest }) => rest),
      };
  } else if (typeof data !== 'object') {
    return null;
  }
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/** Absolute URL on the site origin for a site path; http(s) URLs pass through. */
export function absoluteUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return null;
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  return `${SITE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
}

/** "1347.00" for 134700 cents (schema.org price: no symbol, "." decimal). Integer maths. */
export function centsToPrice(cents) {
  const c = Math.round(Number(cents));
  if (!Number.isFinite(c)) return null;
  const sign = c < 0 ? '-' : '';
  const abs = Math.abs(c);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Tappd as an Organization (home page). */
export function organizationJsonLd() {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}${LOGO_PATH}`,
    sameAs: [...SOCIAL_PROFILES],
  };
}

/** The site itself (home page). No SearchAction: the site has no search results URL. */
export function websiteJsonLd() {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

/** The home page's structured data: Organization + WebSite (one @graph). */
export function homeJsonLd() {
  return [organizationJsonLd(), websiteJsonLd()];
}

function unitPrice(name, cents, months, extra) {
  return {
    '@type': 'UnitPriceSpecification',
    name,
    price: centsToPrice(cents),
    priceCurrency: 'USD',
    referenceQuantity: { '@type': 'QuantitativeValue', value: months, unitCode: 'MON' },
    ...extra,
  };
}

/**
 * The paid slot tiers as a Service with an OfferCatalog: one Offer per paid tier (Standard,
 * Premium, Venue Sponsor) at its quarterly price, with a UnitPriceSpecification per billing
 * term the pricing page shows: quarterly for every tier, annual for Standard and Premium
 * (Venue Sponsor is quarterly only, on a 12-month commitment = billingDuration). `tiers` are
 * GET /api/slots/tiers rows (or lib/slotPricing PUBLISHED_SLOT_TIERS, whichever the page is
 * showing). Returns null when no paid tier has a price.
 */
export function slotOfferCatalogJsonLd(tiers) {
  const list = Array.isArray(tiers) ? tiers : [];
  const offers = PAID_SLOT_TIERS
    .map((key) => list.find((t) => t && t.tier_key === key))
    .filter(Boolean)
    .map((t) => {
      const quarterly = quarterlyCents(t);
      if (!(quarterly > 0)) return null;
      const name = (typeof t.name === 'string' && t.name.trim()) || TIER_NAMES[t.tier_key];
      const sponsor = t.tier_key === 'sponsor';
      const specs = [
        unitPrice('Quarterly', quarterly, 3, sponsor
          ? { billingDuration: { '@type': 'QuantitativeValue', value: 12, unitCode: 'MON' } }
          : undefined),
      ];
      const annual = Math.round(Number(t.annual_price_cents));
      if (!sponsor && annual > 0) specs.push(unitPrice('Annual', annual, 12));
      return {
        '@type': 'Offer',
        name: `${name} slot`,
        description: sponsor
          ? 'Priced per venue, billed quarterly on a 12-month commitment.'
          : 'Priced per venue, billed quarterly or yearly.',
        price: centsToPrice(quarterly),
        priceCurrency: 'USD',
        priceSpecification: specs,
        url: `${SITE_URL}/pricing`,
        itemOffered: { '@type': 'Service', name: `${name} advertising slot` },
      };
    })
    .filter(Boolean);
  if (!offers.length) return null;
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Service',
    name: 'Coaster advertising slots',
    serviceType: 'Advertising on QR coasters at local bars and restaurants',
    url: `${SITE_URL}/pricing`,
    provider: { '@type': 'Organization', '@id': ORGANIZATION_ID, name: SITE_NAME, url: `${SITE_URL}/` },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Advertising slots, priced per venue',
      itemListElement: offers,
    },
  };
}

const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * PostalAddress from the business record's parts (separate fields first, then an address
 * object, the same precedence as BusinessLanding's formatAddress), or null. A usable
 * address needs a region plus a street or a locality; the legacy free-text `address` is
 * never used (it cannot be split into parts reliably).
 */
function businessAddress(b) {
  const fromParts = (street, city, region, postal) => {
    const parts = {
      streetAddress: text(street),
      addressLocality: text(city),
      addressRegion: text(region),
      postalCode: text(postal),
    };
    if (!(parts.addressRegion && (parts.streetAddress || parts.addressLocality))) return null;
    const present = Object.entries(parts).filter(([, v]) => v);
    return { '@type': 'PostalAddress', ...Object.fromEntries(present) };
  };
  if (text(b.street_address) || text(b.city) || text(b.state)) {
    return fromParts(b.street_address, b.city, b.state, b.zip_code);
  }
  if (b.address && typeof b.address === 'object') {
    return fromParts(b.address.street, b.address.city, b.address.state, b.address.zip_code);
  }
  return null;
}

/**
 * A business page (/business/:slug) as a LocalBusiness, or null. Emitted only when a
 * structured PostalAddress can be built (Google requires `address` on LocalBusiness): a
 * region plus a street or a locality. Fields: @id, name, the page URL, address, image (the
 * page's hero image or logo), telephone as stored, and the business's own website (sameAs).
 * No ratings (see the module note) and no opening hours: the stored day_of_week numbering
 * is inconsistent (DB model 0=Monday, Google import and this page 0=Sunday), so publishing
 * hours could state the wrong day.
 */
export function localBusinessJsonLd(business, slug) {
  if (!business || !text(business.name)) return null;
  const address = businessAddress(business);
  if (!address) return null;
  const pageSlug = text(slug) || text(business.slug);
  // Same form as the page's canonical (<SEO url={`/business/${slug}`}>).
  const pageUrl = pageSlug ? `${SITE_URL}/business/${pageSlug}` : null;
  const ld = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'LocalBusiness',
    ...(pageUrl ? { '@id': `${pageUrl}#business` } : {}),
    name: text(business.name),
    ...(pageUrl ? { url: pageUrl } : {}),
    address,
  };
  const image = absoluteUrl(business.image_url || business.logo_url);
  if (image) ld.image = image;
  if (typeof business.phone === 'string' && business.phone.trim()) ld.telephone = business.phone;
  const website = text(business.website);
  if (website && /^https?:\/\//i.test(website)) ld.sameAs = [website];
  return ld;
}
